import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Encabezado } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Cargando,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { actualizar, enviar } from '../api/cliente';
import { fecha, legible } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import type { MensajeListo, RecallVista } from '../api/tipos';

const MOTIVOS_MENSAJE = [
  { valor: 'RecordatorioCita', rotulo: 'Recordatorio de cita' },
  { valor: 'ConfirmacionCita', rotulo: 'Confirmación de cita' },
  { valor: 'PresupuestoEnviado', rotulo: 'Presupuesto enviado' },
  { valor: 'CuotaVencida', rotulo: 'Cuota vencida' },
  { valor: 'Recall', rotulo: 'Control periódico' },
  { valor: 'Cumpleanos', rotulo: 'Cumpleaños' },
];

/**
 * Seguimientos: la lista de trabajo de la recepción. Cada fila tiene una sola acción real
 * —abrir el WhatsApp con el mensaje ya escrito— y el estado avanza con un toque.
 */
export function Seguimientos() {
  const { puede } = useSesion();
  const { datos, cargando, error, recargar } = useDatos<RecallVista[]>('/api/v1/seguimientos');
  const [mensajeando, setMensajeando] = useState<RecallVista | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const { correr, ejecutando } = useAccion();

  const avanzar = async (recall: RecallVista, estado: string, nota?: string) => {
    const hecho = await correr(() =>
      actualizar(`/api/v1/seguimientos/${recall.id}`, { estado, nota: nota ?? null }),
    );

    if (hecho !== null) {
      setAviso(`${recall.paciente}: ${legible(estado).toLowerCase()}.`);
      recargar();
    }
  };

  if (cargando) return <Cargando que="los seguimientos" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  return (
    <>
      <Encabezado
        titulo="Seguimientos"
        detalle="A quién hay que contactar hoy, y con qué mensaje."
      />

      {aviso && (
        <div className="mb-4">
          <Aviso tono="exito" alCerrar={() => setAviso(null)}>
            {aviso}
          </Aviso>
        </div>
      )}

      <Panel>
        {datos?.length === 0 && (
          <Vacio
            titulo="No hay nadie para contactar"
            detalle="Los controles semestrales y los presupuestos sin respuesta van a aparecer acá solos."
          />
        )}

        {datos && datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <caption className="sr-only">Pacientes para contactar</caption>
              <thead>
                <tr>
                  <th scope="col">Paciente</th>
                  <th scope="col">Motivo</th>
                  <th scope="col">Desde</th>
                  <th scope="col">Estado</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {datos.map((recall) => (
                  <tr key={recall.id}>
                    <td>
                      <Link
                        to={`/pacientes/${recall.pacienteId}`}
                        className="font-medium text-tinta-900 hover:text-marca-700"
                      >
                        {recall.paciente}
                      </Link>
                      {recall.telefono && (
                        <span className="cifra block text-dato text-tinta-400">{recall.telefono}</span>
                      )}
                    </td>
                    <td className="text-dato">{legible(recall.motivo)}</td>
                    <td className="cifra whitespace-nowrap text-dato">
                      {fecha(recall.programadoPara)}
                    </td>
                    <td>
                      <Insignia tono={recall.estado === 'Pendiente' ? 'aviso' : 'marca'}>
                        {legible(recall.estado)}
                        {recall.intentos > 0 ? ` · ${recall.intentos}` : ''}
                      </Insignia>
                    </td>
                    <td>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {puede('comunicacion.enviar') && recall.telefono && (
                          <Boton variante="primario" onClick={() => setMensajeando(recall)}>
                            WhatsApp
                          </Boton>
                        )}
                        {puede('agenda.agendar') && (
                          <>
                            <Boton
                              disabled={ejecutando}
                              onClick={() => void avanzar(recall, 'Contactado', 'Se lo llamó')}
                            >
                              Contactado
                            </Boton>
                            <Boton
                              variante="peligro"
                              disabled={ejecutando}
                              onClick={() => void avanzar(recall, 'Descartado', 'No quiere que lo contacten')}
                            >
                              Descartar
                            </Boton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <PrepararMensaje
        recall={mensajeando}
        alCerrar={() => setMensajeando(null)}
        alEnviar={() => {
          setMensajeando(null);
          recargar();
        }}
      />
    </>
  );
}

function PrepararMensaje({
  recall,
  alCerrar,
  alEnviar,
}: {
  recall: RecallVista | null;
  alCerrar: () => void;
  alEnviar: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [motivo, setMotivo] = useState('Recall');
  const [mensaje, setMensaje] = useState<MensajeListo | null>(null);

  const preparar = async () => {
    if (!recall) return;

    const listo = await correr(() =>
      enviar<MensajeListo>('/api/v1/mensajes/preparar', {
        pacienteId: recall.pacienteId,
        motivo,
        registrarEnvio: true,
      }),
    );

    if (listo) {
      setMensaje(listo);
    }
  };

  return (
    <Cajon
      abierto={recall != null}
      titulo={`Mensaje para ${recall?.paciente ?? ''}`}
      alCerrar={() => {
        setMensaje(null);
        alCerrar();
      }}
      pie={
        mensaje?.enlace ? (
          <a href={mensaje.enlace} target="_blank" rel="noreferrer" className="block">
            <Boton
              variante="primario"
              ancho
              onClick={() => {
                setMensaje(null);
                alEnviar();
              }}
            >
              Abrir WhatsApp
            </Boton>
          </a>
        ) : (
          <div className="flex gap-2">
            <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void preparar()}>
              Preparar mensaje
            </Boton>
            <Boton onClick={alCerrar}>Cancelar</Boton>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <Selector etiqueta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
          {MOTIVOS_MENSAJE.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.rotulo}
            </option>
          ))}
        </Selector>

        {mensaje && (
          <>
            <div>
              <p className="etiqueta-campo">Se manda a</p>
              <p className="cifra mt-1 text-sm text-tinta-800">+{mensaje.destino}</p>
            </div>
            <div>
              <p className="etiqueta-campo">Texto</p>
              <p className="mt-1 whitespace-pre-line rounded-lg bg-papel-100 px-3 py-2 text-sm text-tinta-700">
                {mensaje.texto}
              </p>
            </div>
            <p className="text-dato text-tinta-500">
              El envío ya quedó registrado en el historial del paciente. El botón abre WhatsApp con
              el texto puesto; sólo hay que apretar enviar.
            </p>
          </>
        )}

        {!mensaje && (
          <p className="text-dato text-tinta-500">
            El texto sale de la plantilla que configuró la clínica, con el nombre del paciente ya
            reemplazado.
          </p>
        )}

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}
